import type { AdminSelf, PlatformRole } from '@saay/api-client';

/**
 * admin-web permission predicates (FTD §10). Visibility follows the read rights of the D-31 matrix
 * (AC §6.4): an area is shown to a role whose cell is not `–`. test/permissions.spec.ts parses the
 * matrix in docs/API_CONTRACT.md and fails if this table drifts from it. Hiding is a usability aid
 * only; the API authorizes every request and a 403 is still handled.
 */

export type AdminArea =
  | 'dashboard'
  | 'approvals'
  | 'merchants'
  | 'buyers'
  | 'organizations'
  | 'creditPrograms'
  | 'sales'
  | 'payments'
  | 'settlements'
  | 'writeOffs'
  | 'marketing'
  | 'calendar'
  | 'policies'
  | 'platformAccess'
  | 'rolesAuditLedger'
  | 'myCommissions'
  | 'physicalCards'
  | 'wallets';

const STAFF: readonly PlatformRole[] = [
  'SUPER_ADMIN',
  'FINANCE_ADMIN',
  'OPERATIONS_ADMIN',
  'MARKETING_ADMIN',
  'AUDITOR',
];

/** The D-31 matrix rows each area reads (AC §6.4 "Area" column). */
export const AREA_MATRIX_ROWS: Readonly<Record<AdminArea, readonly string[]>> = {
  approvals: [],
  buyers: ['Buyer provisioning, KYC, mobile change, suspension'],
  calendar: ['Manual business-calendar closures'],
  creditPrograms: ['Buyer credit and credit programs'],
  dashboard: [],
  marketing: ['Marketing structures, recipients, assignments'],
  merchants: ['Merchant identity, branches, initial users'],
  myCommissions: [],
  organizations: ['Organizations, memberships, role catalog'],
  payments: ['Sales, contracts, payments, delinquency (view)'],
  // D-117: no matrix row of its own; gated by the numbering-read and export capabilities.
  physicalCards: [],
  policies: [
    'Credit, late-fee, delinquency, grace, installment-bound policies',
    'Wallet policies: limits, top-up fee, multiplier `k` (`wallet`, `wallet-top-up-fee`, `wallet-credit`; D-102, §30.6)',
    'Cash sale default fee policy (D-122: a Super Admin policy, §34.4)',
  ],
  wallets: ['Buyer wallets (view): balances, freezes, top-ups, withdrawals (D-101, §30.15)'],
  platformAccess: ['Platform role assignments'],
  rolesAuditLedger: ['Ledger', 'Audit', 'Platform role assignments'],
  sales: ['Sales, contracts, payments, delinquency (view)'],
  settlements: [
    'Settlement, reconciliation, holds, merchant fraud recovery, write-off, calendar provider sync',
  ],
  writeOffs: [
    'Settlement, reconciliation, holds, merchant fraud recovery, write-off, calendar provider sync',
  ],
};

/** Roles that may see each area. */
export const AREA_ROLES: Readonly<Record<AdminArea, readonly PlatformRole[]>> = {
  // The approval inbox read roles (AC §18.2.6 A1, §18.13 `APPROVAL_READ`): every staff role reads it,
  // filtered by the server (Operations and Marketing see their own and Operations BUYER_CREDIT_CHANGE).
  approvals: STAFF,
  buyers: ['SUPER_ADMIN', 'FINANCE_ADMIN', 'OPERATIONS_ADMIN', 'AUDITOR'],
  calendar: ['SUPER_ADMIN', 'FINANCE_ADMIN', 'OPERATIONS_ADMIN', 'AUDITOR'],
  creditPrograms: ['SUPER_ADMIN', 'FINANCE_ADMIN', 'OPERATIONS_ADMIN', 'AUDITOR'],
  dashboard: STAFF,
  // The Marketer's own data is the separate self view (D-28, FTD §9.16).
  marketing: STAFF,
  merchants: STAFF,
  myCommissions: ['MARKETER'],
  organizations: STAFF,
  payments: STAFF,
  physicalCards: ['SUPER_ADMIN', 'FINANCE_ADMIN', 'OPERATIONS_ADMIN', 'AUDITOR'],
  policies: ['SUPER_ADMIN', 'FINANCE_ADMIN', 'OPERATIONS_ADMIN', 'AUDITOR'],
  platformAccess: ['SUPER_ADMIN'],
  rolesAuditLedger: STAFF,
  sales: STAFF,
  settlements: ['SUPER_ADMIN', 'FINANCE_ADMIN', 'OPERATIONS_ADMIN', 'AUDITOR'],
  wallets: ['SUPER_ADMIN', 'FINANCE_ADMIN', 'OPERATIONS_ADMIN', 'AUDITOR'],
  writeOffs: ['SUPER_ADMIN', 'FINANCE_ADMIN', 'OPERATIONS_ADMIN', 'AUDITOR'],
};

/** Whether the caller holds one of the area's D-31 matrix roles. */
export function holdsAreaRole(self: Pick<AdminSelf, 'roles'>, area: AdminArea): boolean {
  return self.roles.some((role) => (AREA_ROLES[area] as readonly string[]).includes(role));
}

/** Areas that also need a server capability (AC §18.13, UI gating only). */
const AREA_CAPABILITY: Partial<Record<AdminArea, GovernanceCapability>> = {
  approvals: 'APPROVAL_READ',
};

export function canSeeArea(
  self: Pick<AdminSelf, 'roles'> & Partial<Pick<AdminSelf, 'capabilities'>>,
  area: AdminArea,
): boolean {
  // AC §18.10: Marketing Admin is outside the D-31 policies row but reads the approval policy, so it
  // gets a read-only «سیاست‌ها» view with only the types it may read.
  if (area === 'policies' && !holdsAreaRole(self, area)) {
    return POLICY_TYPES.some((type) => canReadPolicy(self, type));
  }
  if (area === 'wallets') {
    return holdsAreaRole(self, area) && hasServerCapability(self, 'WALLET_READ');
  }
  // «کارت‌های فیزیکی» (D-117): shown to whoever reads the numbering or prints cards.
  if (area === 'physicalCards') {
    return canReadNumberingPolicy(self) || canPrintPhysicalCards(self);
  }
  const capability = AREA_CAPABILITY[area];
  return (
    self.roles.some((role) => (AREA_ROLES[area] as readonly string[]).includes(role)) &&
    (capability === undefined || hasCapability(self, capability))
  );
}

// ---------------------------------------------------------------------------------------------
// Stage 3 slice 1 governance (AC §18.2.6, §18.4.8, §18.12, §18.19.6, §18.13). The server derives the
// capabilities of `GET /admin/me` from the family-narrowed roles; the roles below are the contract
// rows, so test/permissions.spec.ts can compare both with docs/API_CONTRACT.md.

/** The §18.13 capabilities the Stage 3 screens gate on (§17.3.4 `MERCHANT_MANAGE` included). */
export type GovernanceCapability =
  | 'APPROVAL_READ'
  | 'APPROVAL_DECIDE'
  | 'APPROVAL_EXECUTE_DIRECT'
  | 'MERCHANT_COMMERCIAL_REQUEST'
  | 'MERCHANT_MANAGE'
  | 'PAYOUT_ACCOUNT_READ'
  | 'PAYOUT_ACCOUNT_MANAGE'
  | 'PLATFORM_ROLE_MANAGE'
  | 'IDENTITY_LOGIN_LOCK_CLEAR'
  | 'AUDIT_READ'
  // Slice 3 (AC §18.13): buyers, credit, organizations, programs, financial adjustments.
  | 'BUYER_MANAGE'
  | 'BUYER_CREDIT_MANAGE'
  | 'ORGANIZATION_READ'
  | 'ORGANIZATION_MANAGE'
  | 'CREDIT_PROGRAM_READ'
  | 'CREDIT_PROGRAM_MANAGE'
  | 'FINANCIAL_ADJUSTMENT_REQUEST'
  // AC §19.6.3: the physical card (UI gating only).
  | 'BUYER_PHYSICAL_CARD_MANAGE'
  | 'PHYSICAL_CARD_EXPORT'
  // AC §31.12 (D-117): the numbering policy screen and its usage reads.
  | 'PHYSICAL_CARD_NUMBERING_READ'
  | 'PHYSICAL_CARD_NUMBERING_MANAGE';

/** AC §18.13 "Granted to" of each capability. */
export const CAPABILITY_ROLES: Readonly<Record<GovernanceCapability, readonly PlatformRole[]>> = {
  APPROVAL_DECIDE: ['SUPER_ADMIN', 'FINANCE_ADMIN'],
  // AC §19.2 (D-61): the forms that execute directly for a manager.
  APPROVAL_EXECUTE_DIRECT: ['SUPER_ADMIN', 'FINANCE_ADMIN'],
  APPROVAL_READ: STAFF,
  AUDIT_READ: STAFF,
  IDENTITY_LOGIN_LOCK_CLEAR: ['SUPER_ADMIN', 'OPERATIONS_ADMIN'],
  MERCHANT_COMMERCIAL_REQUEST: ['SUPER_ADMIN', 'FINANCE_ADMIN'],
  MERCHANT_MANAGE: ['SUPER_ADMIN', 'OPERATIONS_ADMIN'],
  PAYOUT_ACCOUNT_MANAGE: ['SUPER_ADMIN', 'FINANCE_ADMIN'],
  PAYOUT_ACCOUNT_READ: ['SUPER_ADMIN', 'FINANCE_ADMIN', 'OPERATIONS_ADMIN', 'AUDITOR'],
  PLATFORM_ROLE_MANAGE: ['SUPER_ADMIN'],
  BUYER_CREDIT_MANAGE: ['SUPER_ADMIN', 'FINANCE_ADMIN'],
  BUYER_MANAGE: ['SUPER_ADMIN', 'OPERATIONS_ADMIN'],
  CREDIT_PROGRAM_MANAGE: ['SUPER_ADMIN', 'FINANCE_ADMIN'],
  CREDIT_PROGRAM_READ: ['SUPER_ADMIN', 'FINANCE_ADMIN', 'OPERATIONS_ADMIN', 'AUDITOR'],
  FINANCIAL_ADJUSTMENT_REQUEST: ['SUPER_ADMIN', 'FINANCE_ADMIN'],
  BUYER_PHYSICAL_CARD_MANAGE: ['SUPER_ADMIN', 'OPERATIONS_ADMIN'],
  PHYSICAL_CARD_EXPORT: ['SUPER_ADMIN'],
  PHYSICAL_CARD_NUMBERING_MANAGE: ['SUPER_ADMIN'],
  PHYSICAL_CARD_NUMBERING_READ: ['SUPER_ADMIN', 'FINANCE_ADMIN', 'OPERATIONS_ADMIN', 'AUDITOR'],
  ORGANIZATION_MANAGE: ['SUPER_ADMIN', 'OPERATIONS_ADMIN'],
  ORGANIZATION_READ: STAFF,
};

/**
 * Approval-type forms (D-61, AC §19.2). The label follows this capability; the server still decides,
 * so a `201`/`200` executed result and a `202` are both handled.
 */
export function executesApprovalDirectly(self: Partial<Pick<AdminSelf, 'capabilities'>>): boolean {
  return hasCapability(self, 'APPROVAL_EXECUTE_DIRECT');
}

/** «ثبت و اجرا» for a manager, «ارسال برای تأیید» for anyone else. */
export function approvalSubmitLabel(
  self: Partial<Pick<AdminSelf, 'capabilities'>>,
): 'ثبت و اجرا' | 'ارسال برای تأیید' {
  return executesApprovalDirectly(self) ? 'ثبت و اجرا' : 'ارسال برای تأیید';
}

/** A server capability of the self context (unknown values are ignored, AC §17.3.4). */
export function hasCapability(
  self: Partial<Pick<AdminSelf, 'capabilities'>>,
  capability: GovernanceCapability,
): boolean {
  return (self.capabilities as readonly string[] | undefined)?.includes(capability) === true;
}

/** Wallet capabilities from `GET /admin/me` (AC §30.18). They are not §18.13 rows. */
export type WalletCapability = 'WALLET_READ' | 'WALLET_MULTIPLIER_OVERRIDE';

export function hasServerCapability(
  self: Partial<Pick<AdminSelf, 'capabilities'>>,
  capability: WalletCapability,
): boolean {
  return (self.capabilities as readonly string[] | undefined)?.includes(capability) === true;
}

/** The slice 1 operations and the §6.6 roles of each (AC §18.2.6, §18.4, §18.12, §18.19.6). */
export type GovernanceAction =
  | 'listApprovalRequests'
  | 'approveApprovalRequest'
  | 'rejectApprovalRequest'
  | 'cancelApprovalRequest'
  | 'listCapacityAdjustments'
  | 'requestCapacityAdjustment'
  | 'listPlatformRoleAssignments'
  | 'createPlatformRoleAssignment'
  | 'revokePlatformRoleAssignment'
  | 'listAdminAuditEvents'
  | 'getAdminDashboard'
  | 'getAdminMerchantsSummary'
  | 'getAdminBuyersSummary'
  | 'getPlatformRoleAssignmentsSummary'
  // Slice 2: merchant lifecycle (AC §18.4 M1–M14) and payout accounts (§18.5 P4–P10).
  | 'updateMerchant'
  | 'verifyMerchantKyc'
  | 'transitionMerchantStatus'
  | 'updateMerchantBranch'
  | 'listAdminBranchRequests'
  | 'approveBranchRequest'
  | 'rejectBranchRequest'
  | 'updateAdminMerchantUser'
  | 'listCommercialSettingVersions'
  | 'createCommercialSettingVersion'
  | 'listPayoutAccounts'
  | 'requestPayoutAccount'
  | 'verifyPayoutAccount'
  | 'listPayoutAccountChangeRequests'
  | 'applyPayoutAccountChangeRequest'
  | 'rejectPayoutAccountChangeRequest'
  // D-51 (AC §18.21 L1, L2).
  | 'requestLoginLockClearance'
  | 'getLoginLockClearance'
  // Slice 3: buyers (AC §18.6 B1–B10), organizations (§18.8 O1–O18), credit programs (§18.9
  // C1–C11), financial adjustments (§18.11 F1), and the S7/S8 summaries (§18.19.6).
  | 'updateBuyer'
  | 'verifyBuyerIdentity'
  | 'changeBuyerMobile'
  | 'transitionBuyerStatus'
  | 'listBuyerRestrictions'
  | 'createBuyerRestriction'
  | 'releaseBuyerRestriction'
  | 'adjustBaseCredit'
  | 'adjustCreditAllocation'
  | 'listOrganizations'
  | 'createOrganization'
  | 'updateOrganization'
  | 'listOrganizationMemberships'
  | 'createOrganizationMembership'
  | 'updateOrganizationMembership'
  | 'listOrganizationRoles'
  | 'createOrganizationRole'
  | 'listEnrollmentRequests'
  | 'createEnrollmentRequest'
  | 'listEnrollmentRequestMembers'
  | 'appendEnrollmentRequestMembers'
  | 'updateEnrollmentRequestMember'
  | 'validateEnrollmentRequest'
  | 'provisionEnrollmentRequest'
  | 'cancelEnrollmentRequest'
  | 'listCreditPrograms'
  | 'createCreditProgram'
  | 'updateCreditProgram'
  | 'activateCreditProgram'
  | 'createCreditProgramRevision'
  | 'previewCreditProgramTargeting'
  | 'runCreditProgramTargeting'
  | 'listCreditProgramTargetingRuns'
  | 'closeCreditProgram'
  | 'requestFinancialAdjustment'
  | 'getAdminOrganizationsSummary'
  | 'getAdminCreditProgramsSummary'
  // Physical card (AC §19.6.2 K4, K6).
  | 'transitionAdminBuyerPhysicalCard'
  | 'reissueAdminBuyerPhysicalCard'
  // AC §32 (D-118): print batches P1–P4.
  | 'getAdminPhysicalCardPrintReady'
  | 'createAdminPhysicalCardPrintBatch'
  | 'listAdminPhysicalCardPrintBatches'
  | 'downloadAdminPhysicalCardPrintBatch'
  // AC §31.4 (D-117): K7, K8, K9.
  | 'changeAdminBuyerPhysicalCardNumber'
  | 'getAdminPhysicalCardNumberingUsage'
  | 'listAdminPhysicalCardCityUsage'
  // Delinquency cases, waivers, and write-offs (AC §24.11 DQ1–DQ5).
  | 'listDelinquencyCases'
  | 'getDelinquencyCase'
  | 'createLateFeeWaiver'
  | 'listWriteOffCandidates'
  | 'requestWriteOff';

const SUPER_FINANCE: readonly PlatformRole[] = ['SUPER_ADMIN', 'FINANCE_ADMIN'];
const READERS_NO_MARKETING: readonly PlatformRole[] = [
  'SUPER_ADMIN',
  'FINANCE_ADMIN',
  'OPERATIONS_ADMIN',
  'AUDITOR',
];

const SUPER_OPERATIONS: readonly PlatformRole[] = ['SUPER_ADMIN', 'OPERATIONS_ADMIN'];

export const GOVERNANCE_ROLES: Readonly<Record<GovernanceAction, readonly PlatformRole[]>> = {
  activateCreditProgram: SUPER_FINANCE,
  adjustBaseCredit: SUPER_FINANCE,
  adjustCreditAllocation: SUPER_FINANCE,
  appendEnrollmentRequestMembers: SUPER_OPERATIONS,
  cancelEnrollmentRequest: SUPER_OPERATIONS,
  changeBuyerMobile: SUPER_OPERATIONS,
  closeCreditProgram: SUPER_FINANCE,
  createBuyerRestriction: SUPER_OPERATIONS,
  createCreditProgram: SUPER_FINANCE,
  createCreditProgramRevision: SUPER_FINANCE,
  createEnrollmentRequest: SUPER_OPERATIONS,
  createOrganization: SUPER_OPERATIONS,
  createOrganizationMembership: SUPER_OPERATIONS,
  createOrganizationRole: SUPER_OPERATIONS,
  getAdminCreditProgramsSummary: READERS_NO_MARKETING,
  getAdminOrganizationsSummary: STAFF,
  listBuyerRestrictions: READERS_NO_MARKETING,
  listCreditProgramTargetingRuns: READERS_NO_MARKETING,
  listCreditPrograms: READERS_NO_MARKETING,
  listEnrollmentRequestMembers: READERS_NO_MARKETING,
  listEnrollmentRequests: STAFF,
  listOrganizationMemberships: STAFF,
  listOrganizationRoles: STAFF,
  listOrganizations: STAFF,
  previewCreditProgramTargeting: SUPER_FINANCE,
  provisionEnrollmentRequest: SUPER_OPERATIONS,
  releaseBuyerRestriction: SUPER_OPERATIONS,
  requestFinancialAdjustment: SUPER_FINANCE,
  runCreditProgramTargeting: SUPER_FINANCE,
  transitionBuyerStatus: SUPER_OPERATIONS,
  updateBuyer: SUPER_OPERATIONS,
  updateCreditProgram: SUPER_FINANCE,
  updateEnrollmentRequestMember: SUPER_OPERATIONS,
  updateOrganization: SUPER_OPERATIONS,
  updateOrganizationMembership: SUPER_OPERATIONS,
  validateEnrollmentRequest: SUPER_OPERATIONS,
  verifyBuyerIdentity: SUPER_OPERATIONS,
  getAdminPhysicalCardPrintReady: ['SUPER_ADMIN'],
  createAdminPhysicalCardPrintBatch: ['SUPER_ADMIN'],
  listAdminPhysicalCardPrintBatches: ['SUPER_ADMIN'],
  downloadAdminPhysicalCardPrintBatch: ['SUPER_ADMIN'],
  reissueAdminBuyerPhysicalCard: SUPER_OPERATIONS,
  transitionAdminBuyerPhysicalCard: SUPER_OPERATIONS,
  changeAdminBuyerPhysicalCardNumber: SUPER_OPERATIONS,
  getAdminPhysicalCardNumberingUsage: [
    'SUPER_ADMIN',
    'FINANCE_ADMIN',
    'OPERATIONS_ADMIN',
    'AUDITOR',
  ],
  listAdminPhysicalCardCityUsage: ['SUPER_ADMIN', 'FINANCE_ADMIN', 'OPERATIONS_ADMIN', 'AUDITOR'],
  createLateFeeWaiver: SUPER_FINANCE,
  getDelinquencyCase: STAFF,
  listDelinquencyCases: STAFF,
  listWriteOffCandidates: READERS_NO_MARKETING,
  requestWriteOff: SUPER_FINANCE,
  applyPayoutAccountChangeRequest: SUPER_FINANCE,
  approveApprovalRequest: SUPER_FINANCE,
  approveBranchRequest: SUPER_OPERATIONS,
  createCommercialSettingVersion: SUPER_FINANCE,
  getLoginLockClearance: SUPER_OPERATIONS,
  listAdminBranchRequests: STAFF,
  listCommercialSettingVersions: STAFF,
  listPayoutAccountChangeRequests: READERS_NO_MARKETING,
  listPayoutAccounts: READERS_NO_MARKETING,
  rejectBranchRequest: SUPER_OPERATIONS,
  rejectPayoutAccountChangeRequest: SUPER_FINANCE,
  requestLoginLockClearance: SUPER_OPERATIONS,
  requestPayoutAccount: SUPER_FINANCE,
  transitionMerchantStatus: SUPER_OPERATIONS,
  updateAdminMerchantUser: SUPER_OPERATIONS,
  updateMerchant: SUPER_OPERATIONS,
  updateMerchantBranch: SUPER_OPERATIONS,
  verifyMerchantKyc: SUPER_OPERATIONS,
  verifyPayoutAccount: SUPER_FINANCE,
  cancelApprovalRequest: ['SUPER_ADMIN', 'FINANCE_ADMIN', 'OPERATIONS_ADMIN'],
  createPlatformRoleAssignment: ['SUPER_ADMIN'],
  getAdminBuyersSummary: READERS_NO_MARKETING,
  getAdminDashboard: STAFF,
  getAdminMerchantsSummary: STAFF,
  getPlatformRoleAssignmentsSummary: ['SUPER_ADMIN'],
  listAdminAuditEvents: STAFF,
  listApprovalRequests: STAFF,
  listCapacityAdjustments: STAFF,
  listPlatformRoleAssignments: ['SUPER_ADMIN'],
  rejectApprovalRequest: SUPER_FINANCE,
  requestCapacityAdjustment: SUPER_FINANCE,
  revokePlatformRoleAssignment: ['SUPER_ADMIN'],
};

/** The §18.13 capability that gates each command or screen part, when there is one. */
export const GOVERNANCE_CAPABILITY: Partial<Record<GovernanceAction, GovernanceCapability>> = {
  activateCreditProgram: 'CREDIT_PROGRAM_MANAGE',
  adjustBaseCredit: 'BUYER_CREDIT_MANAGE',
  adjustCreditAllocation: 'BUYER_CREDIT_MANAGE',
  appendEnrollmentRequestMembers: 'ORGANIZATION_MANAGE',
  cancelEnrollmentRequest: 'ORGANIZATION_MANAGE',
  changeBuyerMobile: 'BUYER_MANAGE',
  closeCreditProgram: 'CREDIT_PROGRAM_MANAGE',
  createBuyerRestriction: 'BUYER_MANAGE',
  createCreditProgram: 'CREDIT_PROGRAM_MANAGE',
  createCreditProgramRevision: 'CREDIT_PROGRAM_MANAGE',
  createEnrollmentRequest: 'ORGANIZATION_MANAGE',
  createOrganization: 'ORGANIZATION_MANAGE',
  createOrganizationMembership: 'ORGANIZATION_MANAGE',
  createOrganizationRole: 'ORGANIZATION_MANAGE',
  getAdminCreditProgramsSummary: 'CREDIT_PROGRAM_READ',
  getAdminOrganizationsSummary: 'ORGANIZATION_READ',
  listCreditProgramTargetingRuns: 'CREDIT_PROGRAM_READ',
  listCreditPrograms: 'CREDIT_PROGRAM_READ',
  listEnrollmentRequests: 'ORGANIZATION_READ',
  listOrganizationMemberships: 'ORGANIZATION_READ',
  listOrganizationRoles: 'ORGANIZATION_READ',
  listOrganizations: 'ORGANIZATION_READ',
  previewCreditProgramTargeting: 'CREDIT_PROGRAM_MANAGE',
  provisionEnrollmentRequest: 'ORGANIZATION_MANAGE',
  releaseBuyerRestriction: 'BUYER_MANAGE',
  requestFinancialAdjustment: 'FINANCIAL_ADJUSTMENT_REQUEST',
  runCreditProgramTargeting: 'CREDIT_PROGRAM_MANAGE',
  transitionBuyerStatus: 'BUYER_MANAGE',
  updateBuyer: 'BUYER_MANAGE',
  updateCreditProgram: 'CREDIT_PROGRAM_MANAGE',
  updateEnrollmentRequestMember: 'ORGANIZATION_MANAGE',
  updateOrganization: 'ORGANIZATION_MANAGE',
  updateOrganizationMembership: 'ORGANIZATION_MANAGE',
  validateEnrollmentRequest: 'ORGANIZATION_MANAGE',
  verifyBuyerIdentity: 'BUYER_MANAGE',
  getAdminPhysicalCardPrintReady: 'PHYSICAL_CARD_EXPORT',
  createAdminPhysicalCardPrintBatch: 'PHYSICAL_CARD_EXPORT',
  listAdminPhysicalCardPrintBatches: 'PHYSICAL_CARD_EXPORT',
  downloadAdminPhysicalCardPrintBatch: 'PHYSICAL_CARD_EXPORT',
  reissueAdminBuyerPhysicalCard: 'BUYER_PHYSICAL_CARD_MANAGE',
  transitionAdminBuyerPhysicalCard: 'BUYER_PHYSICAL_CARD_MANAGE',
  changeAdminBuyerPhysicalCardNumber: 'BUYER_PHYSICAL_CARD_MANAGE',
  getAdminPhysicalCardNumberingUsage: 'PHYSICAL_CARD_NUMBERING_READ',
  listAdminPhysicalCardCityUsage: 'PHYSICAL_CARD_NUMBERING_READ',
  applyPayoutAccountChangeRequest: 'PAYOUT_ACCOUNT_MANAGE',
  approveApprovalRequest: 'APPROVAL_DECIDE',
  approveBranchRequest: 'MERCHANT_MANAGE',
  createCommercialSettingVersion: 'MERCHANT_COMMERCIAL_REQUEST',
  getLoginLockClearance: 'IDENTITY_LOGIN_LOCK_CLEAR',
  listPayoutAccountChangeRequests: 'PAYOUT_ACCOUNT_READ',
  listPayoutAccounts: 'PAYOUT_ACCOUNT_READ',
  rejectBranchRequest: 'MERCHANT_MANAGE',
  rejectPayoutAccountChangeRequest: 'PAYOUT_ACCOUNT_MANAGE',
  requestLoginLockClearance: 'IDENTITY_LOGIN_LOCK_CLEAR',
  requestPayoutAccount: 'PAYOUT_ACCOUNT_MANAGE',
  transitionMerchantStatus: 'MERCHANT_MANAGE',
  updateAdminMerchantUser: 'MERCHANT_MANAGE',
  updateMerchant: 'MERCHANT_MANAGE',
  updateMerchantBranch: 'MERCHANT_MANAGE',
  verifyMerchantKyc: 'MERCHANT_MANAGE',
  verifyPayoutAccount: 'PAYOUT_ACCOUNT_MANAGE',
  createPlatformRoleAssignment: 'PLATFORM_ROLE_MANAGE',
  getPlatformRoleAssignmentsSummary: 'PLATFORM_ROLE_MANAGE',
  listAdminAuditEvents: 'AUDIT_READ',
  listApprovalRequests: 'APPROVAL_READ',
  listPlatformRoleAssignments: 'PLATFORM_ROLE_MANAGE',
  rejectApprovalRequest: 'APPROVAL_DECIDE',
  requestCapacityAdjustment: 'MERCHANT_COMMERCIAL_REQUEST',
  revokePlatformRoleAssignment: 'PLATFORM_ROLE_MANAGE',
};

/** A slice 1 operation: the role row of §6.6 and, where one exists, the §18.13 capability. */
export function canUse(
  self: Pick<AdminSelf, 'roles'> & Partial<Pick<AdminSelf, 'capabilities'>>,
  action: GovernanceAction,
): boolean {
  const capability = GOVERNANCE_CAPABILITY[action];
  return (
    self.roles.some((role) => (GOVERNANCE_ROLES[action] as readonly string[]).includes(role)) &&
    (capability === undefined || hasCapability(self, capability))
  );
}

/**
 * Approve and reject buttons (AC §18.2.4): the caller may decide (`APPROVAL_DECIDE`) **and** the
 * server's per-request `decidable` flag is true (it applies "nobody approves their own", linked
 * identities, and the grantor rule). The API still refuses with 403, which the screen shows.
 */
export function canDecide(
  self: Pick<AdminSelf, 'roles'> & Partial<Pick<AdminSelf, 'capabilities'>>,
  request: { readonly decidable: boolean; readonly status: string },
): boolean {
  return (
    request.status === 'PENDING' && request.decidable && canUse(self, 'approveApprovalRequest')
  );
}

/** Cancel (A5): only the requester, and only while the request is pending (AC §18.2.4). */
export function canCancelApproval(
  self: Pick<AdminSelf, 'roles' | 'user'>,
  request: { readonly requestedBy: { readonly userId: string }; readonly status: string },
): boolean {
  return (
    request.status === 'PENDING' &&
    request.requestedBy.userId === self.user.id &&
    canUse(self, 'cancelApprovalRequest')
  );
}

/** AuditDomain values (AC §18.12) in display order. */
export const AUDIT_DOMAINS = ['FINANCIAL', 'OPERATIONS', 'MARKETING', 'PLATFORM'] as const;
export type AuditDomainValue = (typeof AUDIT_DOMAINS)[number];

/** The domain each domain-scoped role reads (AC §18.12 R3, M-17). */
const DOMAIN_OF_ROLE: Partial<Record<PlatformRole, AuditDomainValue>> = {
  FINANCE_ADMIN: 'FINANCIAL',
  MARKETING_ADMIN: 'MARKETING',
  OPERATIONS_ADMIN: 'OPERATIONS',
};

/**
 * The audit domains the audit viewer offers as filters: Super Admin and Auditor read every domain;
 * Finance, Operations, and Marketing Admin read only their own (a filter outside it would return an
 * empty page, AC §18.12). The server scopes the rows either way.
 */
export function auditDomainsFor(self: Pick<AdminSelf, 'roles'>): readonly AuditDomainValue[] {
  if (self.roles.some((role) => role === 'SUPER_ADMIN' || role === 'AUDITOR')) {
    return AUDIT_DOMAINS;
  }
  const own = new Set(self.roles.flatMap((role) => DOMAIN_OF_ROLE[role] ?? []));
  return AUDIT_DOMAINS.filter((domain) => own.has(domain));
}

/** The ledger tab of «حسابرسی و دفترکل» (D-31 row "Ledger": Super, Finance, Auditor). */
export function canReadLedger(self: Pick<AdminSelf, 'roles'>): boolean {
  return holds(self, ['SUPER_ADMIN', 'FINANCE_ADMIN', 'AUDITOR']);
}

// ---------------------------------------------------------------------------------------------
// Stage 2.3 setup actions (AC §17.5 "Roles" column, §17.3.4 capabilities). test/permissions.spec.ts
// parses both tables from docs/API_CONTRACT.md and fails if these drift from them.

export type AdminAction =
  | 'createMerchant'
  | 'createMerchantBranch'
  | 'createAdminMerchantUser'
  | 'replaceInstallmentPlanActivations'
  | 'createMerchantCategory'
  | 'provisionBuyer';

export const ACTION_ROLES: Readonly<Record<AdminAction, readonly PlatformRole[]>> = {
  createAdminMerchantUser: ['SUPER_ADMIN', 'OPERATIONS_ADMIN'],
  createMerchant: ['SUPER_ADMIN', 'OPERATIONS_ADMIN'],
  createMerchantBranch: ['SUPER_ADMIN', 'OPERATIONS_ADMIN'],
  createMerchantCategory: ['SUPER_ADMIN', 'OPERATIONS_ADMIN'],
  provisionBuyer: ['SUPER_ADMIN', 'OPERATIONS_ADMIN'],
  replaceInstallmentPlanActivations: ['SUPER_ADMIN', 'FINANCE_ADMIN'],
};

function holds(self: Pick<AdminSelf, 'roles'>, roles: readonly PlatformRole[]): boolean {
  return self.roles.some((role) => (roles as readonly string[]).includes(role));
}

export function canPerform(self: Pick<AdminSelf, 'roles'>, action: AdminAction): boolean {
  return holds(self, ACTION_ROLES[action]);
}

/** The policy types (AC §18.10, including `payment-fee` from §25.3) in the §18.10 order. */
export const POLICY_TYPES = [
  'installment-bounds',
  'confirmation',
  'delinquency',
  'late-fee',
  'credit',
  'guarantee',
  'buyer-provisioning',
  'approval',
  'payment-fee',
  'cash-sale-fee',
] as const;

export type StagePolicyType = (typeof POLICY_TYPES)[number];

const POLICY_READERS_DEFAULT: readonly PlatformRole[] = [
  'SUPER_ADMIN',
  'FINANCE_ADMIN',
  'OPERATIONS_ADMIN',
  'AUDITOR',
];

/** AC §18.10 "Read roles" per type: `approval` is the only type Marketing Admin reads. */
export const POLICY_READ_ROLES: Readonly<Record<StagePolicyType, readonly PlatformRole[]>> = {
  approval: STAFF,
  'buyer-provisioning': POLICY_READERS_DEFAULT,
  confirmation: POLICY_READERS_DEFAULT,
  credit: POLICY_READERS_DEFAULT,
  delinquency: POLICY_READERS_DEFAULT,
  guarantee: POLICY_READERS_DEFAULT,
  'installment-bounds': POLICY_READERS_DEFAULT,
  'late-fee': POLICY_READERS_DEFAULT,
  'payment-fee': POLICY_READERS_DEFAULT,
  'cash-sale-fee': POLICY_READERS_DEFAULT,
};

/** AC §18.10 "Manage roles" per type (the `POLICY_*_MANAGE` capabilities of §18.13). */
export const POLICY_MANAGE_ROLES: Readonly<Record<StagePolicyType, readonly PlatformRole[]>> = {
  approval: ['SUPER_ADMIN'],
  'buyer-provisioning': SUPER_OPERATIONS,
  confirmation: ['SUPER_ADMIN'],
  credit: SUPER_FINANCE,
  delinquency: SUPER_FINANCE,
  guarantee: SUPER_OPERATIONS,
  'installment-bounds': SUPER_FINANCE,
  'late-fee': SUPER_FINANCE,
  'payment-fee': ['SUPER_ADMIN'],
  'cash-sale-fee': ['SUPER_ADMIN'],
};

export function canReadPolicy(self: Pick<AdminSelf, 'roles'>, type: StagePolicyType): boolean {
  return holds(self, POLICY_READ_ROLES[type]);
}

export function canManagePolicy(self: Pick<AdminSelf, 'roles'>, type: StagePolicyType): boolean {
  return holds(self, POLICY_MANAGE_ROLES[type]);
}

/** The three wallet policy types (AC §30.6). Kept off `POLICY_TYPES`, which mirrors §18.10. */
export const WALLET_POLICY_TYPES = ['wallet', 'wallet-top-up-fee', 'wallet-credit'] as const;

export type WalletPolicyType = (typeof WALLET_POLICY_TYPES)[number];

const WALLET_POLICY_READERS: readonly PlatformRole[] = [
  'SUPER_ADMIN',
  'FINANCE_ADMIN',
  'OPERATIONS_ADMIN',
  'AUDITOR',
];

export function canReadWalletPolicy(
  self: Pick<AdminSelf, 'roles'>,
  _type: WalletPolicyType,
): boolean {
  return holds(self, WALLET_POLICY_READERS);
}

/** Super Admin writes wallet policies directly (D-102). Finance, Operations, and Auditor read them. */
export function canManageWalletPolicy(self: Pick<AdminSelf, 'roles'>): boolean {
  return holds(self, ['SUPER_ADMIN']);
}

/**
 * The `physical-card-numbering` policy (D-117, AC §31.6). Kept off `POLICY_TYPES`, which mirrors the
 * §18.10 table. Read by Super, Finance, Operations, and Auditor; managed by the Super Admin alone.
 */
export const NUMBERING_POLICY_TYPE = 'physical-card-numbering' as const;

export type NumberingPolicyType = typeof NUMBERING_POLICY_TYPE;

const NUMBERING_READERS: readonly PlatformRole[] = [
  'SUPER_ADMIN',
  'FINANCE_ADMIN',
  'OPERATIONS_ADMIN',
  'AUDITOR',
];

/** The numbering policy tab and the usage reads K8, K9 (`PHYSICAL_CARD_NUMBERING_READ`). */
export function canReadNumberingPolicy(
  self: Pick<AdminSelf, 'roles'> & Partial<Pick<AdminSelf, 'capabilities'>>,
): boolean {
  return holds(self, NUMBERING_READERS) && hasCapability(self, 'PHYSICAL_CARD_NUMBERING_READ');
}

/** A new numbering version (`PHYSICAL_CARD_NUMBERING_MANAGE`, Super Admin). */
export function canManageNumberingPolicy(
  self: Pick<AdminSelf, 'roles'> & Partial<Pick<AdminSelf, 'capabilities'>>,
): boolean {
  return holds(self, ['SUPER_ADMIN']) && hasCapability(self, 'PHYSICAL_CARD_NUMBERING_MANAGE');
}

/**
 * «چاپ کارت»: the print batches (AC §32 P1–P4, D-118), `PHYSICAL_CARD_EXPORT`, Super Admin only.
 * The four operations share one row, so the tab needs all of them.
 */
export function canPrintPhysicalCards(
  self: Pick<AdminSelf, 'roles'> & Partial<Pick<AdminSelf, 'capabilities'>>,
): boolean {
  return (
    canUse(self, 'getAdminPhysicalCardPrintReady') &&
    canUse(self, 'createAdminPhysicalCardPrintBatch') &&
    canUse(self, 'listAdminPhysicalCardPrintBatches') &&
    canUse(self, 'downloadAdminPhysicalCardPrintBatch')
  );
}

/** The tabs of «کارت‌های فیزیکی» (D-117). */
export type PhysicalCardsTab = 'summary' | 'cities' | 'settings' | 'print' | 'history';

/**
 * The «کارت‌های فیزیکی» tabs the caller may open, in order: the numbering readers get the summary,
 * the cities, the settings, and the history; the print tab needs `PHYSICAL_CARD_EXPORT`.
 */
export function physicalCardsTabs(
  self: Pick<AdminSelf, 'roles'> & Partial<Pick<AdminSelf, 'capabilities'>>,
): readonly PhysicalCardsTab[] {
  const read = canReadNumberingPolicy(self);
  return [
    ...(read ? (['summary', 'cities', 'settings'] as const) : []),
    ...(canPrintPhysicalCards(self) ? (['print'] as const) : []),
    ...(read ? (['history'] as const) : []),
  ];
}

/** Buyer wallet, timeline, top-ups, and the summary (AC §30.15, §30.28). */
export function canReadWallet(
  self: Pick<AdminSelf, 'roles'> & Partial<Pick<AdminSelf, 'capabilities'>>,
): boolean {
  return holds(self, WALLET_POLICY_READERS) && hasServerCapability(self, 'WALLET_READ');
}

/** The per-buyer `k` override is the Super Admin's alone (D-109). */
export function canOverrideWalletMultiplier(
  self: Pick<AdminSelf, 'roles'> & Partial<Pick<AdminSelf, 'capabilities'>>,
): boolean {
  return holds(self, ['SUPER_ADMIN']) && hasServerCapability(self, 'WALLET_MULTIPLIER_OVERRIDE');
}

/** `payoutAccount` is absent for Marketing Admin (AC §6.4 payout accounts: none). */
export function canSeePayoutAccount(self: Pick<AdminSelf, 'roles'>): boolean {
  return holds(self, ['SUPER_ADMIN', 'FINANCE_ADMIN', 'OPERATIONS_ADMIN', 'AUDITOR']);
}

/** AC §26 / D-31: settlement commands and provider-sync are Super/Finance only. */
export function canManageSettlements(self: Pick<AdminSelf, 'roles'>): boolean {
  return holds(self, SUPER_FINANCE);
}

/** Manual calendar closures add Operations Admin to the settlement managers. */
export function canManageCalendarClosures(self: Pick<AdminSelf, 'roles'>): boolean {
  return holds(self, ['SUPER_ADMIN', 'FINANCE_ADMIN', 'OPERATIONS_ADMIN']);
}

// ---------------------------------------------------------------------------------------------
// Stage 6 marketing (AC §28.4). Gating follows the operation role rows. The §28.16 capabilities
// (MARKETING_READ, MARKETING_MANAGE, COMMISSION_PAYOUT_READ, COMMISSION_PAYOUT_MANAGE) are not in
// the generated self context yet, so the screens use these rows, which name the same roles.

const MARKETING_READERS: readonly PlatformRole[] = [
  'SUPER_ADMIN',
  'FINANCE_ADMIN',
  'OPERATIONS_ADMIN',
  'MARKETING_ADMIN',
  'AUDITOR',
  'MARKETER',
];
const MARKETING_STAFF_READERS: readonly PlatformRole[] = [
  'SUPER_ADMIN',
  'FINANCE_ADMIN',
  'OPERATIONS_ADMIN',
  'MARKETING_ADMIN',
  'AUDITOR',
];
const MARKETING_MANAGERS: readonly PlatformRole[] = ['SUPER_ADMIN', 'MARKETING_ADMIN'];

export type MarketingAction =
  | 'listMarketingRecipients'
  | 'createMarketingRecipient'
  | 'getMarketingRecipient'
  | 'updateMarketingRecipient'
  | 'listMarketingStructures'
  | 'createMarketingStructure'
  | 'getMarketingStructure'
  | 'createMarketingStructureVersion'
  | 'updateMarketingStructureVersion'
  | 'publishMarketingStructureVersion'
  | 'listMarketingAssignments'
  | 'createMarketingAssignment'
  | 'getMarketingAssignment'
  | 'updateMarketingAssignment'
  | 'previewMarketingAssignmentImpact'
  | 'activateMarketingAssignment'
  | 'deactivateMarketingAssignment'
  | 'listMarketingCommissions'
  | 'listCommissionPayoutAttempts'
  | 'getCommissionPayoutAttempt'
  | 'retryCommissionPayoutAttempt'
  | 'executeCommissionPayouts'
  | 'listCommissionPayoutHolds'
  | 'createCommissionPayoutHold'
  | 'releaseCommissionPayoutHold'
  | 'getAdminMarketingSummary'
  | 'getMarketerCommissionSummary';

export const MARKETING_ACTION_ROLES: Readonly<Record<MarketingAction, readonly PlatformRole[]>> = {
  activateMarketingAssignment: MARKETING_MANAGERS,
  createCommissionPayoutHold: SUPER_FINANCE,
  createMarketingAssignment: MARKETING_MANAGERS,
  createMarketingRecipient: MARKETING_MANAGERS,
  createMarketingStructure: MARKETING_MANAGERS,
  createMarketingStructureVersion: MARKETING_MANAGERS,
  deactivateMarketingAssignment: MARKETING_MANAGERS,
  executeCommissionPayouts: SUPER_FINANCE,
  getAdminMarketingSummary: MARKETING_STAFF_READERS,
  getCommissionPayoutAttempt: MARKETING_READERS,
  getMarketingAssignment: MARKETING_READERS,
  getMarketingRecipient: MARKETING_READERS,
  getMarketingStructure: MARKETING_READERS,
  getMarketerCommissionSummary: ['MARKETER'],
  listCommissionPayoutAttempts: MARKETING_READERS,
  listCommissionPayoutHolds: MARKETING_STAFF_READERS,
  listMarketingAssignments: MARKETING_READERS,
  listMarketingCommissions: MARKETING_READERS,
  listMarketingRecipients: MARKETING_READERS,
  listMarketingStructures: MARKETING_READERS,
  previewMarketingAssignmentImpact: MARKETING_MANAGERS,
  publishMarketingStructureVersion: MARKETING_MANAGERS,
  releaseCommissionPayoutHold: SUPER_FINANCE,
  retryCommissionPayoutAttempt: SUPER_FINANCE,
  updateMarketingAssignment: MARKETING_MANAGERS,
  updateMarketingRecipient: MARKETING_MANAGERS,
  updateMarketingStructureVersion: MARKETING_MANAGERS,
};

/** Whether the caller may invoke a §28 operation (UI gating; the API still authorizes). */
export function canMarket(self: Pick<AdminSelf, 'roles'>, action: MarketingAction): boolean {
  return holds(self, MARKETING_ACTION_ROLES[action]);
}

/** A principal whose only platform role is MARKETER (own-recipient scope, AC §28.4). */
export function isMarketerOnly(self: Pick<AdminSelf, 'roles'>): boolean {
  return self.roles.length > 0 && self.roles.every((role) => role === 'MARKETER');
}
